import { FC } from "react";

import { type CourseEnrollmentData } from "api/api-data-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";

type CourseEnrollmentTableProps = {
  data: CourseEnrollmentData[];
};

export const CourseEnrollmentTable: FC<CourseEnrollmentTableProps> = ({ data }) => {
  return (
    <div className="overflow-x-auto">
      <div className="max-h-[330px] overflow-y-auto">
        <table className="mds-table w-full">
          <thead className="sticky top-0 z-10 bg-white shadow-sm">
            <tr>
              <th className="w-[90px]">
                <Tooltip place="top" text={t("course_enrollment_report.columns.term_tooltip")}>
                  {t("course_enrollment_report.columns.term")}
                </Tooltip>
              </th>

              <th className="w-[72px]">
                <Tooltip
                  place="top"
                  text={t("course_enrollment_report.columns.course_code_tooltip")}
                >
                  {t("course_enrollment_report.columns.course_code")}
                </Tooltip>
              </th>

              <th className="w-[120px]">
                <Tooltip
                  place="top"
                  text={t("course_enrollment_report.columns.course_title_tooltip")}
                >
                  {t("course_enrollment_report.columns.course_title")}
                </Tooltip>
              </th>

              <th className="w-[60px]">
                <Tooltip place="top" text={t("course_enrollment_report.columns.role_tooltip")}>
                  {t("course_enrollment_report.columns.role")}
                </Tooltip>
              </th>

              <th className="w-[100px]">
                <Tooltip place="top" text={t("course_enrollment_report.columns.name_tooltip")}>
                  {t("course_enrollment_report.columns.name")}
                </Tooltip>
              </th>

              <th className="w-[100px]">
                <Tooltip place="top" text={t("course_enrollment_report.columns.email_tooltip")}>
                  {t("course_enrollment_report.columns.email")}
                </Tooltip>
              </th>

              <th className="w-[70px]">
                <Tooltip
                  place="top"
                  text={t("course_enrollment_report.columns.disabled_status_tooltip")}
                >
                  {t("course_enrollment_report.columns.disabled_status")}
                </Tooltip>
              </th>

              <th className="w-[72px] px-1">
                <Tooltip
                  place="top"
                  text={t("course_enrollment_report.columns.external_user_id_tooltip")}
                >
                  {t("course_enrollment_report.columns.external_user_id")}
                </Tooltip>
              </th>

              <th className="w-[72px]">
                <Tooltip
                  place="top"
                  text={t("course_enrollment_report.columns.external_course_id_tooltip")}
                >
                  {t("course_enrollment_report.columns.external_course_id")}
                </Tooltip>
              </th>

              <th className="w-[64px]">
                <Tooltip
                  place="top"
                  text={t("course_enrollment_report.columns.invite_status_tooltip")}
                >
                  {t("course_enrollment_report.columns.invite_status")}
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row) => (
              <tr key={`${row.email}-${row.course_title}`}>
                <td className="w-[90px] truncate pr-[1px]">
                  <Tooltip place="left-start" text={row.term} wrapperClassName="truncate">
                    {row.term}
                  </Tooltip>
                </td>

                <td className="w-[72px] truncate pr-[1px]">
                  <Tooltip place="left-start" text={row.course_code} wrapperClassName="truncate">
                    {row.course_code}
                  </Tooltip>
                </td>

                <td className="w-[120px] pr-[1px]">
                  <Tooltip place="left-start" text={row.course_title}>
                    {row.course_title}
                  </Tooltip>
                </td>

                <td className="w-[60px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={t(`course_enrollment_report.roles.${row.role}`)}
                    wrapperClassName="truncate"
                  >
                    <span className="inline-flex items-center rounded-full py-1 text-xs font-medium">
                      {t(`course_enrollment_report.roles.${row.role}`)}
                    </span>
                  </Tooltip>
                </td>

                <td className="w-[100px] truncate pr-[1px]">
                  <Tooltip place="left-start" text={row.name} wrapperClassName="truncate">
                    {row.name}
                  </Tooltip>
                </td>

                <td className="w-[100px] truncate pr-[1px]">
                  <Tooltip place="left-start" text={row.email} wrapperClassName="truncate">
                    {row.email}
                  </Tooltip>
                </td>

                <td className="w-[70px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={t(
                      `course_enrollment_report.disabled_status_values.${row.disabled_status}`,
                    )}
                    wrapperClassName="truncate"
                  >
                    {t(`course_enrollment_report.disabled_status_values.${row.disabled_status}`)}
                  </Tooltip>
                </td>

                <td className="w-[72px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={row.external_user_id || "-"}
                    wrapperClassName="truncate"
                  >
                    {row.external_user_id || "-"}
                  </Tooltip>
                </td>

                <td className="w-[72px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={row.external_course_id || "-"}
                    wrapperClassName="truncate"
                  >
                    {row.external_course_id || "-"}
                  </Tooltip>
                </td>

                <td className="w-[60px] truncate pr-[1px]">
                  <Tooltip
                    place="left-start"
                    text={t(`course_enrollment_report.invite_status.${row.invite_status}`)}
                    wrapperClassName="truncate"
                  >
                    {t(`course_enrollment_report.invite_status.${row.invite_status}`)}
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
